import { Typography } from '@euler/app/components/typography';
import { useCurrentUserDangerously } from '@euler/app/flows/auth/context';
import { GeneralNavHeader } from '@euler/app/tv/components/GeneralNavHeader';
import { kScreenContentPadding } from '@euler/app/tv/functions/constants';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { Img } from '@euler/components/image/AdvancedImage';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useFetchAsyncState } from '@euler/functions/useFetchAsyncState';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useServiceFactory } from '@euler/services/factory';
import { onErrorIgnore } from '@euler/utils';
import { useCallback, useEffect } from 'react';
import { View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const kLogoSize = 360;
const kLogoPadding = 20;

const useStoreStaticMapImageUrl = () => {
  const { storeService } = useServiceFactory();
  const query = useCallback(() => {
    return storeService.getStaticMapImageUrl({
      width: kLogoSize + 2 * kLogoPadding,
      height: kLogoSize + 2 * kLogoPadding,
      scale: 1,
      zoom: 17,
      markerSize: 64,
    });
  }, [storeService]);
  const [state, fetch] = useFetchAsyncState(query, { status: 'loading' });
  useEffect(() => {
    fetch().catch(onErrorIgnore);
  }, [fetch]);
  return state;
};

const useOfficialAccountQrcodeImageUrl = () => {
  const { storeService } = useServiceFactory();
  const query = useCallback(() => {
    return storeService.getOfficialAccountQrcodeImageUrl({
      size: 400,
      margin: 3,
      logo: !__DEV__,
    });
  }, [storeService]);
  const [state, fetch] = useFetchAsyncState(query, { status: 'loading' });
  useEffect(() => {
    fetch().catch(onErrorIgnore);
  }, [fetch]);
  return state;
};

export const StoreInfoScreen = wrapNavigatorScreen(
  () => {
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    const { store, org } = useCurrentUserDangerously();
    const mapImageUrlState = useStoreStaticMapImageUrl();
    const qrcodeImageUrlState = useOfficialAccountQrcodeImageUrl();
    const {
      storeInfoScreen: {
        storeNameTextColor,
        orgNameTextColor,
        descriptionTextColor,
        imagePlaceholderColor,
      },
    } = usePresentationTheme();

    return (
      <View
        css={`
          flex: 1;
          padding-bottom: ${insets.bottom + rpx(40)}px;
        `}
      >
        <GeneralNavHeader title="门店介绍" />
        <View
          css={`
            margin-left: ${rpx(kScreenContentPadding)}px;
            margin-right: ${rpx(kScreenContentPadding)}px;
            flex: 1;
            align-items: center;
            justify-content: center;
          `}
        >
          <Typography.Label
            bold
            size={rpx(48)}
            lineHeight={rpx(64)}
            color={storeNameTextColor}
          >
            {store.name}
          </Typography.Label>
          <Typography.Label
            thin
            size={rpx(24)}
            lineHeight={rpx(48)}
            color={orgNameTextColor}
          >
            {org.name}
          </Typography.Label>
          <HStack
            justifyContent="center"
            alignItems="center"
            css={`
              margin-top: ${rpx(64)}px;
            `}
          >
            <Img
              uri={org.logoImgUrl}
              css={`
                width: ${rpx(kLogoSize + 2 * kLogoPadding)}px;
                height: ${rpx(kLogoSize + 2 * kLogoPadding)}px;
              `}
            />
            {qrcodeImageUrlState.status === 'success' && (
              <Img
                uri={qrcodeImageUrlState.result}
                css={`
                  width: ${rpx(kLogoSize + 2 * kLogoPadding)}px;
                  height: ${rpx(kLogoSize + 2 * kLogoPadding)}px;
                  margin-left: ${rpx(40)}px;
                  background-color: ${imagePlaceholderColor};
                `}
              />
            )}
            {mapImageUrlState.status === 'success' && (
              <Img
                resizeMode="cover"
                uri={mapImageUrlState.result}
                css={`
                  width: ${rpx(kLogoSize + 2 * kLogoPadding)}px;
                  height: ${rpx(kLogoSize + 2 * kLogoPadding)}px;
                  margin-left: ${rpx(40)}px;
                  background-color: ${imagePlaceholderColor};
                `}
              />
            )}
          </HStack>
          {store.introduction || org.introduction ? (
            <View
              css={`
                width: ${rpx((kLogoSize + 2 * kLogoPadding) * 3 + 80)}px;
                margin-top: ${rpx(64)}px;
              `}
            >
              <Typography.Label
                thin
                color={descriptionTextColor}
                size={rpx(20)}
                lineHeight={rpx(36)}
              >
                {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
                {store.introduction || org.introduction}
              </Typography.Label>
            </View>
          ) : null}
        </View>
      </View>
    );
  },
  {
    headerShown: false,
  },
);
